import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { interpolate, useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';

import { useFocusEffect } from '@react-navigation/native';

import { animationConfig } from '@/components/Switch';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';

const MARGIN = 4;
const HEIGHT = 56;
const BORDER_WIDTH = 1;
const PADDING_X = 16;

const VERSUS = 0;
const PRACTICE = 1;

export type Modes = '1v1' | 'practice';

type ModeSwitchProp = {
    activeMode: Modes;
    onModeChange: (mode: Modes) => void;
};

export const ModeSwitch = ({ activeMode, onModeChange }: ModeSwitchProp) => {
    const { t } = useTranslation('arcade_game_modes');
    const dimensions = useWindowDimensions();
    const width = dimensions.width - PADDING_X * 2;
    const switchTranslate = useSharedValue(Number(activeMode === 'practice'));

    const animate = useCallback(
        (toValue: number) => {
            switchTranslate.value = withSpring(toValue, animationConfig);
        },
        [switchTranslate]
    );

    useFocusEffect(
        useCallback(() => {
            if (activeMode === '1v1') {
                animate(VERSUS);
            } else if (activeMode === 'practice') {
                animate(PRACTICE);
            }
        }, [activeMode, animate])
    );

    const buttonStyle = useAnimatedStyle(() => {
        return {
            transform: [
                {
                    translateX: interpolate(switchTranslate.value, [VERSUS, PRACTICE], [MARGIN, width / 2], 'clamp'),
                },
            ],
        };
    });

    return (
        <Animated.View style={styles.container}>
            <Row flex={1} zIndex={1}>
                <Pressable style={styles.pressable} onPress={() => onModeChange('1v1')}>
                    <Text
                        variant="titleMedium2"
                        color={activeMode === '1v1' ? ds.colorToken.text.primary : 'neutral300'}
                    >
                        {t('1v1')}
                    </Text>
                </Pressable>
                <Pressable style={styles.pressable} onPress={() => onModeChange('practice')}>
                    <Text
                        variant="titleMedium2"
                        color={activeMode === 'practice' ? ds.colorToken.text.primary : 'neutral300'}
                    >
                        {t('practice')}
                    </Text>
                </Pressable>
            </Row>
            <Animated.View
                style={[
                    styles.button,
                    {
                        width: width / 2 - MARGIN - BORDER_WIDTH,
                    },
                    buttonStyle,
                ]}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        height: HEIGHT,
        justifyContent: 'center',
        borderRadius: 100,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        shadowColor: designSystem.colors.neutral800,
        shadowOffset: {
            width: 0,
            height: 0,
        },
        shadowOpacity: 0.6,
        shadowRadius: 15,
        elevation: 8,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    pressable: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    button: {
        height: HEIGHT - MARGIN * 2 - BORDER_WIDTH * 2,
        backgroundColor: ds.colorValue.background.neutral.strong,
        borderRadius: 28,
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowColor: designSystem.colors.neutral900,
        shadowOpacity: 0.25,
        shadowRadius: 2,
        elevation: 1,
        position: 'absolute',
    },
});
